import { useState, useRef, useCallback, useEffect } from "react";
import type { ReactElement, CSSProperties } from "react";

/**
 * VideoPlayer component props
 */
interface VideoPlayerProps {
  videoUrl: string;
  posterUrl: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  onEnded?: () => void;
  onPlay?: () => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * VideoPlayer Component
 *
 * A video player for animated story page videos.
 * When autoPlay is true, video plays automatically and loops.
 * Video is always muted (required for browser autoplay policies).
 *
 * Used for animated story page videos (generated from key scenes).
 */
const VideoPlayer = ({
  videoUrl,
  posterUrl,
  autoPlay = true,
  loop = true,
  muted = true,
  onEnded,
  onPlay,
  className = "",
  style,
}: VideoPlayerProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Attempt to play video when component mounts or videoUrl changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !autoPlay) return;

    setIsLoading(true);
    setHasError(false);

    // Reset and play
    video.currentTime = 0;
    video.play()
      .then(() => {
        onPlay?.();
      })
      .catch((err) => {
        console.error("Failed to autoplay video:", err);
        setHasError(true);
      });
  }, [videoUrl, autoPlay, onPlay]);

  const handleVideoEnded = useCallback(() => {
    if (!loop) {
      onEnded?.();
    }
  }, [loop, onEnded]);

  const handleCanPlay = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  // If video fails to load, show poster image as fallback
  if (hasError) {
    return (
      <div className={`relative overflow-hidden ${className}`} style={style}>
        <img
          src={posterUrl}
          alt="Story illustration"
          className="w-full h-full object-cover"
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      {/* Video Element - always visible, poster shown while loading */}
      <video
        ref={videoRef}
        src={videoUrl}
        poster={posterUrl}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline
        preload="auto"
        className="w-full h-full object-cover"
        onEnded={handleVideoEnded}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onWaiting={() => setIsLoading(true)}
        onPlaying={() => setIsLoading(false)}
      />

      {/* Loading spinner overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
